import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

export const CONTRACT_KIND: Record<string, string> = { clt: "CLT", pj: "PJ", partner: "Sócio", intern: "Estágio", freelancer: "Autônomo" };
export const DEFAULT_HOURS: Record<string, number> = { clt: 220, intern: 150, pj: 160, partner: 160, freelancer: 160 };
export const RUN_KIND: Record<string, string> = {
  monthly: "Mensal",
  thirteenth_1: "13º, 1ª parcela",
  thirteenth_2: "13º, 2ª parcela",
  vacation: "Férias",
  termination: "Rescisão",
};
export const RUN_STATUS: StatusMap = {
  open: { label: "aberta", tone: "info" },
  calculated: { label: "calculada", tone: "warn" },
  closed: { label: "fechada", tone: "ok" },
};
export const EMPLOYEE_STATUS: StatusMap = { active: { label: "ativo", tone: "ok" }, terminated: { label: "desligado", tone: "neutral" } };
export const LEAVE_KIND: Record<string, string> = {
  sick: "Doença",
  accident: "Acidente",
  maternity: "Licença-maternidade",
  paternity: "Licença-paternidade",
  unpaid: "Não remunerado",
  other: "Outro",
};
export const TERMINATION_KIND: Record<string, string> = {
  sem_justa_causa: "Dispensa sem justa causa",
  justa_causa: "Dispensa por justa causa",
  pedido_demissao: "Pedido de demissão",
  acordo_mutuo: "Acordo (art. 484-A)",
  termino_contrato: "Término de contrato",
  rescisao_indireta: "Rescisão indireta",
};
export const NOTICE_KIND: Record<string, string> = { indenizado: "Indenizado", trabalhado: "Trabalhado", dispensado: "Dispensado" };
export const TERMINATION_STATUS: StatusMap = {
  calculated: { label: "calculada", tone: "warn" },
  approved: { label: "aprovada", tone: "ok" },
  canceled: { label: "cancelada", tone: "neutral" },
};
export const ITEM_KIND: Record<string, string> = { earning: "Provento", deduction: "Desconto", employer: "Encargo" };

export interface EmployeeView {
  employee_id: string;
  person_id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  registration: string | null;
  status: "active" | "terminated";
  hired_on: string;
  terminated_on: string | null;
  ir_dependents: number;
  contract_id: string | null;
  contract_kind: string | null;
  issuer_id: string | null;
  starts_on: string | null;
  ends_on: string | null;
  monthly_hours: number | null;
  contract_status: string | null;
  position_code: string | null;
  position_title: string | null;
  role_key: string | null;
}

export interface Position {
  id: string;
  code: string;
  title: string;
  role_key: string;
  cbo: string | null;
  active: boolean;
}

export interface Compensation {
  id: string;
  contract_id: string;
  effective_from: string;
  base_monthly_cents: number;
  benefits_monthly_cents: number;
  reason: string;
  created_at: string;
}

export interface CostPerHour {
  contract_id: string;
  employee_id: string;
  person_id: string;
  contract_kind: string;
  role_key: string;
  position_title: string;
  effective_from: string;
  base_monthly_cents: number;
  benefits_monthly_cents: number;
  monthly_hours: number;
  monthly_cost_cents: number;
  cost_per_hour_cents: number;
}

export interface RoleCost {
  role_key: string;
  contracts: number;
  monthly_hours: number;
  monthly_cost_cents: number;
  cost_per_hour_cents: number | null;
}

export interface PayrollRun {
  id: string;
  issuer_id: string | null;
  competence: string;
  kind: string;
  status: "open" | "calculated" | "closed";
  gross_cents: number;
  deductions_cents: number;
  net_cents: number;
  employer_cents: number;
  employees: number;
  tables_used: Record<string, string>;
  calculated_at: string | null;
  closed_at: string | null;
}

export interface PayrollItem {
  id: string;
  run_id: string;
  employee_id: string;
  rubric_code: string;
  kind: "earning" | "deduction" | "employer";
  description: string | null;
  amount_cents: number;
  quantity: number | null;
  source: "manual" | "calc";
}

export interface Rubric {
  code: string;
  name: string;
  kind: "earning" | "deduction" | "employer";
  system: boolean;
  active: boolean;
}

export interface Leave {
  id: string;
  employee_id: string;
  kind: string;
  starts_on: string;
  ends_on: string | null;
  status: "active" | "canceled";
  notes: string | null;
}

export interface Vacation {
  id: string;
  employee_id: string;
  acquisition_start: string;
  acquisition_end: string;
  starts_on: string;
  days: number;
  sold_days: number;
  vacation_cents: number;
  one_third_cents: number;
  allowance_cents: number;
  allowance_third_cents: number;
  inss_cents: number;
  irrf_cents: number;
  fgts_cents: number;
  net_cents: number;
  pay_due_on: string;
  status: "scheduled" | "canceled";
  cancel_reason: string | null;
}

export interface Termination {
  id: string;
  employee_id: string;
  kind: string;
  notice_kind: string;
  terminated_on: string;
  base_monthly_cents: number;
  breakdown: Record<string, number | string>;
  gross_cents: number;
  deductions_cents: number;
  net_cents: number;
  fgts_fine_cents: number;
  pay_due_on: string;
  status: "calculated" | "approved" | "canceled";
  approved_at: string | null;
  created_at: string;
}

export interface Payslip {
  run_id: string;
  competence: string;
  run_kind: string;
  run_status: string;
  employee_id: string;
  gross_cents: number;
  deductions_cents: number;
  net_cents: number;
  employer_cents: number;
  items: Array<{ code: string; kind: string; description: string | null; amount_cents: number; quantity: number | null }>;
}

export interface HrSettings {
  payroll_regime: "simples" | "normal";
  employer_inss_bp: number;
  rat_bp: number;
  third_parties_bp: number;
  fgts_bp: number;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "hr.install_defaults": { args: { p_org: U }; returns: number };
    "hr.set_settings": { args: { p_org: U; p_patch: Partial<HrSettings> }; returns: HrSettings };
    "hr.upsert_position": { args: { p_org: U; p_code: string; p_title: string; p_role_key: string; p_cbo?: string | null; p_active?: boolean }; returns: U };
    "hr.hire": {
      args: {
        p_org: U; p_person: U; p_kind: string; p_position: U; p_starts_on: string; p_base_monthly_cents: number; p_monthly_hours?: number | null;
        p_benefits_monthly_cents?: number; p_issuer?: U | null; p_registration?: string | null; p_cpf?: string | null; p_ir_dependents?: number;
      };
      returns: { employee_id: U; contract_id: U };
    };
    "hr.update_employee": { args: { p_employee: U; p_patch: { registration?: string | null; cpf?: string | null; ir_dependents?: number } }; returns: null };
    "hr.set_compensation": {
      args: { p_contract: U; p_effective_from: string; p_base_monthly_cents: number; p_benefits_monthly_cents?: number; p_reason?: string | null };
      returns: U;
    };
    "hr.change_position": { args: { p_contract: U; p_position: U }; returns: null };
    "hr.end_contract": { args: { p_contract: U; p_ends_on: string; p_reason: string }; returns: null };
    "hr.refresh_role_costs": { args: { p_org: U }; returns: number };
    "hr.register_leave": { args: { p_employee: U; p_kind: string; p_starts_on: string; p_ends_on?: string | null; p_notes?: string | null }; returns: U };
    "hr.end_leave": { args: { p_leave: U; p_ends_on: string }; returns: null };
    "hr.cancel_leave": { args: { p_leave: U }; returns: null };
    "hr.schedule_vacation": { args: { p_employee: U; p_starts_on: string; p_days: number; p_sold_days?: number; p_acquisition_start?: string | null }; returns: U };
    "hr.cancel_vacation": { args: { p_vacation: U; p_reason: string }; returns: null };
    "hr.calculate_termination": {
      args: { p_employee: U; p_kind: string; p_notice_kind: string; p_terminated_on: string; p_fgts_balance_cents?: number | null };
      returns: U;
    };
    "hr.approve_termination": { args: { p_termination: U }; returns: null };
    "hr.cancel_termination": { args: { p_termination: U; p_reason: string }; returns: null };
    "hr.open_run": { args: { p_org: U; p_competence: string; p_kind?: string; p_issuer?: U | null }; returns: U };
    "hr.add_item": {
      args: { p_run: U; p_employee: U; p_rubric_code: string; p_amount_cents: number; p_quantity?: number | null; p_description?: string | null };
      returns: U;
    };
    "hr.remove_item": { args: { p_item: U }; returns: null };
    "hr.calculate_run": { args: { p_run: U }; returns: Record<string, unknown> };
    "hr.close_run": { args: { p_run: U }; returns: Record<string, unknown> };
  }
}

export function useEmployees(orgId: string) {
  return useQuery({
    queryKey: ["hr", "employees", orgId],
    queryFn: () => unwrap<EmployeeView[]>(from("hr", "v_employees").select("*").eq("org_id", orgId).order("full_name")),
  });
}

export function usePositions(orgId: string) {
  return useQuery({
    queryKey: ["hr", "positions", orgId],
    queryFn: () => unwrap<Position[]>(from("hr", "positions").select("*").eq("org_id", orgId).order("title")),
  });
}

export function useRubrics(orgId: string) {
  return useQuery({
    queryKey: ["hr", "rubrics", orgId],
    queryFn: () => unwrap<Rubric[]>(from("hr", "rubrics").select("code, name, kind, system, active").eq("org_id", orgId).order("name")),
  });
}

export function useHrSettings(orgId: string) {
  return useQuery({
    queryKey: ["hr", "settings", orgId],
    queryFn: async () => (await unwrap<HrSettings[]>(from("hr", "settings").select("*").eq("org_id", orgId)))[0] ?? null,
  });
}

export const BREAKDOWN_LABEL: Array<[string, string]> = [
  ["salary_balance_cents", "Saldo de salário"],
  ["notice_cents", "Aviso prévio indenizado"],
  ["vacation_expired_cents", "Férias vencidas"],
  ["vacation_proportional_cents", "Férias proporcionais"],
  ["vacation_third_cents", "1/3 de férias"],
  ["thirteenth_cents", "13º proporcional"],
  ["notice_deduction_cents", "(−) Desconto do aviso"],
  ["inss_salary_cents", "(−) INSS sobre saldo"],
  ["inss_thirteenth_cents", "(−) INSS sobre 13º"],
  ["irrf_salary_cents", "(−) IRRF sobre saldo"],
  ["irrf_thirteenth_cents", "(−) IRRF sobre 13º"],
  ["fgts_deposit_cents", "FGTS do mês (depósito)"],
  ["fgts_fine_cents", "Multa do FGTS"],
];
